import PromotionsTable from '@/components/admin/promotions';
import {
  Button,
  message,
  Modal,
  Popover,
  Row,
  Space,
  Switch,
  Typography,
} from 'antd';
import './index.scss';
import { SvgIcon } from '@/components/icon';
import PromotionForm from '@/components/admin/promotions/PromotionForm';
import { useState } from 'react';
import { Promotion } from '@/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createPromotion,
  deletePromotionById,
  getAdminPromotions,
  updatePromotionById,
} from '@/api/promotions';
import { format } from 'date-fns';

export default function AdminPromotions() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [visible, setVisible] = useState(false);
  const [visibleAdd, setVisibleAdd] = useState(false);
  const [visibleDelete, setVisibleDelete] = useState(false);
  const [modalDeleteInfo, setModalDeleteInfo] = useState({
    id: '',
    name: '',
    description: '',
  });
  const [modalFormData, setModalFormData] = useState<Promotion | null>(null);

  // States for filters (status, effect, date range)
  const [promotionFilters, setPromotionFilters] = useState<any>({
    status: 'All',
    effect: 'All',
    affected: 'All',
    startDate: null,
    endDate: null,
    page,
    limit,
  });

  const queryClient = useQueryClient();

  const { data } = useQuery<{
    total: number;
    page: number;
    limit: number;
    promotions: (Promotion & { key: string })[];
  }>({
    queryKey: ['promotions', promotionFilters],
    queryFn: () => getAdminPromotions(promotionFilters),
  });
  const { mutateAsync: deletePromotion } = useMutation({
    mutationFn: ({ id }: { id: string }) => deletePromotionById(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      message.success('Promotion deleted successfully!');
    },
    onError: (error: any) => {
      console.log(error);
      message.error(
        error.message || 'Promotion delete failed. Please try again.',
      );
    },
    onSettled: () => {
      setVisibleDelete(false);
    },
  });
  const { mutateAsync: updatePromotion } = useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      name?: string;
      description?: string;
      effect?: string;
      affected?: string;
      status?: string;
      startDate?: Date;
      endDate?: Date;
    }) => updatePromotionById(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      message.success('Promotion updated successfully!');
    },
    onError: (error: any) => {
      console.log(error);
      message.error(
        error.message || 'Promotion update failed. Please try again.',
      );
    },
  });

  const { mutateAsync: createPromotionMutation } = useMutation({
    mutationFn: (data: Promotion) => createPromotion(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      message.success('Promotion created successfully!');
    },
    onError: (error: any) => {
      console.log(error);
      message.error(
        error.message || 'Promotion create failed. Please try again.',
      );
    },
  });

  const popoverContent = (promotion: Promotion) => (
    <div className="promotion-popover-content">
      <Button
        type="text"
        className="promotion-link"
        onClick={() => {
          setVisible(true);
          setModalFormData(promotion);
        }}
      >
        Edit
      </Button>
      <Button
        type="text"
        className="promotion-link"
        onClick={() => {
          setVisibleDelete(true);
          setModalDeleteInfo(promotion);
        }}
      >
        Delete
      </Button>
    </div>
  );

  const onSwitchChange = async (id: string, checked: boolean) => {
    const promotion = data?.promotions?.find(
      (promotion) => promotion.id === id,
    );
    if (!promotion) return;

    await updatePromotion({
      id: promotion.id,
      status: checked ? 'active' : 'inactive',
    });
  };

  const columns = [
    {
      title: '',
      dataIndex: '',
      key: 'switch',
      render: (_: any, entity: any) => (
        <Switch
          defaultChecked={entity.status === 'active'}
          className="promotion-switch"
          onChange={(checked) => onSwitchChange(entity.id, checked)}
          size="small"
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Typography.Link style={{ fontWeight: 500, color: '#1C8271' }}>
          {text}
        </Typography.Link>
      ),
    },
    {
      title: 'Effect',
      dataIndex: 'effect',
      key: 'effect',
    },
    {
      title: 'Affected',
      dataIndex: 'affected',
      key: 'affected',
    },
    {
      title: 'Starting Date & Time',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography.Text>
            {format(new Date(date), 'd MMM, yyyy, h:mm a')}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'Ending Date & Time',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography.Text>
            {format(new Date(date), 'd MMM, yyyy, h:mm a')}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: status === 'active' ? '#1C8271' : '#F68B8B',
            }}
          />
          <Typography.Text>
            {status === 'active' ? 'Running' : 'Paused'}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: '',
      dataIndex: '',
      key: 'menu',
      render: (_: any, entity: any) => (
        <Popover
          placement="bottom"
          content={() => popoverContent(entity)}
          trigger="click"
          arrow={false}
        >
          <Button type="link">
            <SvgIcon type={'threeDots'} />
          </Button>
        </Popover>
      ),
    },
  ];

  const onCancel = () => {
    setVisible(false);
  };
  const onSave = async (values: Partial<Promotion>) => {
    if (!modalFormData) return;
    await updatePromotion({
      id: modalFormData?.id,
      ...values,
    });
    setVisible(false);
  };
  const onButtonClick = () => {
    setVisibleAdd(true);
  };
  const onDeleteClicked = async () => {
    await deletePromotion({
      id: modalDeleteInfo.id,
    });
    setVisibleDelete(false);
  };
  return (
    <>
      <Row
        justify={'space-between'}
        align={'middle'}
        style={{ marginBottom: 16 }}
      >
        <Typography.Title
          level={2}
          style={{
            margin: '0 0 24px',
          }}
        >
          Promotions Management
        </Typography.Title>
        <Button
          type={'primary'}
          onClick={onButtonClick}
          className="add-new-promotion-button"
        >
          Add New Promotion
        </Button>
      </Row>
      <PromotionsTable
        data={data?.promotions || []}
        columns={columns}
        promotionFilters={promotionFilters}
        setPromotionFilters={setPromotionFilters}
        total={data?.total || 0}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />
      <Modal
        title={<div className="modal-title">Edit Promotion</div>}
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={460}
      >
        <PromotionForm
          onCancel={onCancel}
          onSave={onSave}
          data={modalFormData}
        />
      </Modal>
      <Modal
        title={<div className="modal-title">Add New Promotion</div>}
        open={visibleAdd}
        onCancel={() => setVisibleAdd(false)}
        footer={null}
        width={460}
      >
        <PromotionForm
          onCancel={() => setVisibleAdd(false)}
          onSave={(data) => {
            createPromotionMutation(data);
            setVisibleAdd(false);
          }}
          data={null}
        />
      </Modal>
      <Modal
        className="promotion-delete-modal"
        title={
          <div className="modal-title">{`Delete ${modalDeleteInfo.name}`}</div>
        }
        open={visibleDelete}
        onCancel={() => setVisibleDelete(false)}
        footer={null}
        width={460}
      >
        <Space style={{ marginBottom: 16 }}>
          <Typography.Text>
            {`Are you sure you want to delete ${modalDeleteInfo.name}? This action
            cannot be undone, and the item will be permanently removed.`}
          </Typography.Text>
        </Space>
        <Space style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            type="primary"
            className={'promotion-add-modal-button'}
            onClick={onDeleteClicked}
          >
            Delete
          </Button>
          <Button
            className={'promotion-cancel-button'}
            onClick={() => setVisibleDelete(false)}
          >
            Cancel
          </Button>
        </Space>
      </Modal>
    </>
  );
}
